import React from 'react';
import {
    Container,
    Paper,
    Button,
    Typography,
    Box,
    styled,
    alpha,
    Divider,
    useTheme,
} from '@mui/material';
import {
    Description,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const BackgroundContainer = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    background: theme.palette.mode === 'dark'
        ? `
      radial-gradient(circle at 20% 80%, rgba(102, 126, 234, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(118, 75, 162, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(102, 126, 234, 0.04) 0%, transparent 50%),
      linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)
    `
        : `
      radial-gradient(circle at 20% 80%, rgba(102, 126, 234, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(118, 75, 162, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(102, 126, 234, 0.05) 0%, transparent 50%),
      linear-gradient(135deg, #FAFBFC 0%, #F4F6F8 100%)
    `,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: 16,
    boxShadow: theme.shadows[8],
    backdropFilter: 'blur(10px)',
    background: alpha(theme.palette.background.paper, 0.9),
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    maxWidth: 800,
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 12,
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(1.5),
    fontSize: '1rem',
}));

const PrivacyPolicy: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    const handleBack = () => {
        // Se veio do SignUpForm, volta para o login com a aba de cadastro ativa e os dados
        if (location.state?.fromSignUp) {
            navigate('/login', { 
                state: { 
                    showSignUp: true,
                    formData: location.state.formData
                } 
            });
        } else {
            // Caso contrário, volta para a página anterior
            navigate(-1);
        }
    };

    const handleContinueToDPA = () => {
        navigate('/dpa', { 
            state: location.state 
        });
    };

    return (
        <BackgroundContainer>
            <Container maxWidth="md">
                <StyledPaper elevation={3}>
                    <Box mb={3}>
                        <Box display="flex" alignItems="center" mb={2}>
                            <Description sx={{ mr: 2, color: 'primary.main' }} />
                            <Typography variant="h4" component="h1" fontWeight="bold">
                                Política de Privacidade
                            </Typography>
                        </Box>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    <Box sx={{ '& > *': { mb: 3 } }}>
                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                                1) QUEM SOMOS E A QUE SE APLICA
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Esta Política descreve como a GAIA PLUS AI LTDA (CNPJ 57.986.228/0001-10), com sede na Av. Paulista, 807, Conj. 1614, Bela Vista, São Paulo/SP – CEP 01311-915, trata dados pessoais relacionados ao uso do site e da plataforma LIATALK AI (“Plataforma” ou “LIATALK AI”).
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Observamos a Lei nº 13.709/2018 (LGPD) e demais normas aplicáveis.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Caso o Titular se encontre fora do Brasil, esta Política também se aplica ao tratamento de dados realizado pela GAIA PLUS em território nacional
                            </Typography>
                        </Box>

        
                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                                2) PAPÉIS NO TRATAMENTO
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Como regra, atuamos como Controladora, definindo as finalidades e os meios do tratamento. Em projetos específicos, poderemos atuar como Operadora para clientes/parceiros, seguindo as instruções do Controlador e observando a LGPD e medidas de segurança. Nos casos em que atuamos como Operadora, firmamos contratos específicos de tratamento de dados com cláusulas de confidencialidade, segurança e auditoria.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                                3) QUAIS DADOS COLETAMOS
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Podemos coletar e tratar:
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                                <li>
                                    <Typography variant="body2" color="text.secondary">Identificação e contato: nome, e-mail, celular, país, CPF e/ou CNPJ;</Typography>
                                </li>
                                <li>
                                    <Typography variant="body2" color="text.secondary">Faturamento e cobrança: dados de pagamento (processados por provedores de pagamento);</Typography>
                                </li>
                                <li>
                                    <Typography variant="body2" color="text.secondary">Uso da Plataforma: dados de acesso, registros de atividades, endereço IP, data/hora, páginas/recursos acessados, dispositivo, sistema e navegador;</Typography>
                                </li>
                                <li>
                                    <Typography variant="body2" color="text.secondary">Conteúdos fornecidos por você: mensagens, arquivos e demais informações inseridas nas conversas/integrações da Plataforma; e</Typography>
                                </li>
                                <li>
                                    <Typography variant="body2" color="text.secondary">Interações com IA: registros de interações com modelos de inteligência artificial, incluindo mensagens, prompts e respostas geradas, utilizados exclusivamente para fins de melhoria, segurança e auditoria do sistema.</Typography>
                                </li>
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                                4) PARA QUE USAMOS OS DADOS (FINALIDADES)
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Utilizamos dados pessoais para:
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                                <li><Typography variant="body2" color="text.secondary">criar e gerenciar contas, prover acesso e prestar os serviços da Plataforma;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">atender solicitações e oferecer suporte;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">processar pagamentos e cumprir obrigações fiscais/contábeis;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">melhorar desempenho, segurança e corrigir erros (bugs);</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">realizar métricas e estatísticas de uso;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">enviar comunicações institucionais e de marketing (com opção de opt-out a qualquer momento);</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">cumprir obrigações legais/regulatórias e exercer direitos em processos;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">conduzir atividades necessárias à operação regular do negócio.</Typography></li>
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                                5) BASE LEGAL
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                As bases legais podem incluir: execução de contrato, cumprimento de obrigação legal, legítimo interesse (p. ex., segurança, métricas, melhoria do serviço) e consentimento quando exigido (especialmente em comunicações de marketing ou casos específicos).
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                As bases legais adotadas encontram-se descritas em tabela de correspondência que relaciona o tipo de dado, a finalidade do tratamento e a respectiva base jurídica, conforme previsto na LGPD.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                                6) CRIANÇAS E ADOLESCENTES (BRASIL)
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Nossos serviços não se destinam a crianças (até 12 anos incompletos). O tratamento de dados de crianças somente ocorrerá, quando aplicável, com consentimento específico e em destaque de pelo menos um dos pais ou responsável legal e sempre com foco no melhor interesse da criança.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Para adolescentes (12 a 18 anos), seguimos as bases legais da LGPD e fornecemos informações claras e adequadas.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Disponibilizamos um canal específico para que pais ou responsáveis legais possam solicitar a exclusão de dados de menores de idade (bruno@liatalk.ai).
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                                7) COM QUEM COMPARTILHAMOS
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Podemos compartilhar dados:
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                                <li><Typography variant="body2" color="text.secondary">com provedores de serviços (p. ex., hospedagem, pagamentos, suporte, auditoria), estritamente para a execução contratada, sob obrigações de confidencialidade e segurança;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">com parceiros comerciais para benefícios/divulgação de serviços, observando esta Política e a LGPD;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">com autoridades, mediante ordem legal ou para defesa de direitos;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">em transferências internacionais, adotando salvaguardas adequadas (cláusulas contratuais, avaliações de adequação, medidas complementares) e, quando necessário, consentimento do titular.</Typography></li>
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                                8) SEGURANÇA DA INFORMAÇÃO
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Adotamos medidas técnicas e administrativas proporcionais aos riscos, controle de acesso, registro de eventos e práticas de prevenção, detecção e resposta a incidentes. Acesso indevido sujeita o infrator a medidas disciplinares e legais. Em caso de incidente, envidaremos esforços para mitigar efeitos e comunicar quando exigido pela legislação.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Mantemos políticas internas de gestão de acesso, criptografia em repouso e em trânsito, além de revisões periódicas de vulnerabilidades e auditorias de conformidade.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                                9) RETENÇÃO E ELIMINAÇÃO
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Mantemos dados pelo tempo necessário ao cumprimento das finalidades, de obrigações legais/regulatórias e para exercício regular de direitos. Após esses prazos, eliminamos ou anonimizamos os dados de forma segura. Você pode solicitar eliminação, observadas as hipóteses legais de retenção.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Os dados pessoais são armazenados pelo prazo necessário ao cumprimento das finalidades, podendo ser anonimizados após cinco anos do término da relação contratual, conforme o caso.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                                10) SEUS DIREITOS (art. 18 da LGPD)
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Você pode solicitar, entre outros:
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                                <li><Typography variant="body2" color="text.secondary">confirmação do tratamento e acesso aos dados;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">correção de dados incompletos/inexatos/desatualizados;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em desconformidade;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">portabilidade a outro fornecedor, quando aplicável;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">informação sobre compartilhamentos;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">informação sobre a possibilidade de negar consentimento e suas consequências;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">revogação do consentimento; e</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">informações sobre a forma e duração do tratamento.</Typography></li>
                            </Box>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Para exercer direitos, envie uma solicitação para: bruno@liatalk.ai.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                                11) COOKIES E TECNOLOGIAS SIMILARES
                            </Typography>
                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">11.1 O que são?</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Cookies são pequenos arquivos armazenados no seu dispositivo que permitem lembrar preferências, aprimorar a navegação, realizar métricas e reforçar a segurança. Tecnologias similares (ex.: local storage, pixels) podem ser utilizadas com finalidades equivalentes.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Ressalvamos que o Usuário poderá revogar o consentimento de cookies a qualquer momento por meio do banner de gestão de preferências ou das configurações de seu navegador.
                            </Typography>

                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">11.2 Como usamos (categorias)?</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>Utilizamos cookies nas seguintes categorias:</Typography>
                            <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                                <li><Typography variant="body2" color="text.secondary">Essenciais (estritamente necessários): viabilizam funcionalidades básicas do site/Plataforma (login, segurança, roteamento de páginas);</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">Desempenho/Analytics: medem uso e desempenho para melhorar estabilidade, velocidade e usabilidade;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">Funcionais: registram preferências (idioma, layout, última sessão), oferecendo experiência mais personalizada; e</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">Marketing/Publicidade (quando aplicável): mensuram campanhas, melhoram a relevância de comunicações e podem personalizar conteúdos.</Typography></li>
                            </Box>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                A instalação de cookies não essenciais (Analytics, Funcionais e Marketing) depende do seu consentimento, que pode ser revogado a qualquer momento.
                            </Typography>

                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">11.3 Gestão de consentimento (banner e preferências)</Typography>
                            <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                                <li><Typography variant="body2" color="text.secondary">No primeiro acesso exibimos um banner com as opções: “Aceitar todos”, “Rejeitar não essenciais” e “Configurar” por categoria.</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">Você pode alterar suas escolhas a qualquer momento pelo link “Gerenciar cookies” disponível no rodapé da Plataforma.</Typography></li>
                            </Box>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Texto sugerido para o banner:
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Usamos cookies para operar o site, analisar desempenho e personalizar sua experiência. Você pode aceitar todos, rejeitar não essenciais ou configurar por categoria. [Configurar cookies]
                            </Typography>

                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">11.4 Transparência sobre cookies</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Mantemos uma visão geral por categoria. Se desejar transparência máxima, podemos publicar uma lista detalhada de cookies (Nome, Provedor, Finalidade, Duração) em página dedicada.
                            </Typography>

                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">11.5 Cookies de terceiros e transferências internacionais</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Alguns cookies podem ser definidos por terceiros que prestam serviços à LIATALK AI (p. ex., hospedagem, analytics, atendimento). Quando houver transferência internacional, adotamos salvaguardas contratuais e técnicas compatíveis com a LGPD. Os terceiros recebem apenas o estritamente necessário para a finalidade contratada.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                O Usuário poderá revogar o consentimento de cookies a qualquer momento por meio do banner de gestão de preferências ou das configurações de seu navegador.
                            </Typography>

                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">11.6 Como controlar cookies no navegador</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Você pode bloquear/remover cookies nas configurações do navegador. A desativação de cookies essenciais pode impactar o funcionamento do site/Plataforma.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Guias úteis:
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                                <li><Typography variant="body2" color="text.secondary">Chrome: https://support.google.com/chrome/answer/95647?hl=pt-BR</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">Firefox (cookies): https://support.mozilla.org/pt-BR/kb/como-ativar-desativar-cookies</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">Firefox (cache): https://support.mozilla.org/pt-BR/kb/como-limpar-cache-firefox</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">Safari: https://support.apple.com/pt-br/guide/safari/sfri11471/mac</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">Opera: https://help.opera.com/pt-br/latest/web-preferences/#cookies</Typography></li>
                            </Box>

                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">11.7 Atualizações</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Esta seção poderá ser atualizada para refletir mudanças legais, técnicas ou operacionais. A versão vigente ficará publicada em https://platform.liatalk.ai/ e, quando aplicável, comunicaremos pelos canais informados no cadastro.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Esta Política é revisada periodicamente e contém, no rodapé, a data de sua última atualização.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                                12) ATUALIZAÇÕES DESTA POLÍTICA
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Podemos atualizar esta Política para refletir mudanças legais, técnicas ou operacionais. A versão vigente será sempre publicada em https://platform.liatalk.ai/ e, quando cabível, comunicaremos pelos canais informados no cadastro. Constará no rodapé da presente a data da última atualização.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                                13) FALE COM A GENTE
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Dúvidas sobre privacidade e proteção de dados: bruno@liatalk.ai.
                            </Typography>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Box textAlign="center">
                        <StyledButton
                            variant="contained"
                            sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
                            onClick={handleContinueToDPA}
                            size="large"
                        >
                            Continuar
                        </StyledButton>
                    </Box>
                </StyledPaper>
            </Container>
        </BackgroundContainer>
    );
};

export default PrivacyPolicy;

